import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/api';

// ─── OAuth ────────────────────────────────────────────────────────────────────

export const fetchGoogleOAuthUrl = createAsyncThunk(
  'googleAds/fetchOAuthUrl',
  async (redirectUri, { rejectWithValue }) => {
    try {
      const res = await api.get('/marketing/google-ads/oauth/url', { params: { redirectUri } });
      return res.data;
    } catch (e) {
      // Pass full response data so the UI can detect setup errors (503)
      return rejectWithValue(e.response?.data || { error: 'Failed to get OAuth URL' });
    }
  }
);

export const checkGoogleConnection = createAsyncThunk(
  'googleAds/checkConnection',
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get('/marketing/google-ads/oauth/status');
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.error || 'Failed to check connection');
    }
  }
);

export const disconnectGoogle = createAsyncThunk(
  'googleAds/disconnect',
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.delete('/marketing/google-ads/oauth/disconnect');
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.error || 'Failed to disconnect');
    }
  }
);

// ─── Accounts ────────────────────────────────────────────────────────────────

export const fetchGoogleAccounts = createAsyncThunk(
  'googleAds/fetchAccounts',
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get('/marketing/google-ads/accounts');
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.error || 'Failed to fetch accounts');
    }
  }
);

// ─── Campaigns ────────────────────────────────────────────────────────────────

export const fetchGoogleCampaigns = createAsyncThunk(
  'googleAds/fetchCampaigns',
  async (params = {}, { rejectWithValue }) => {
    try {
      const res = await api.get('/marketing/google-ads/campaigns', { params });
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.error || 'Failed to fetch campaigns');
    }
  }
);

export const createGoogleCampaign = createAsyncThunk(
  'googleAds/createCampaign',
  async ({ customerId, campaignData }, { rejectWithValue }) => {
    try {
      const res = await api.post('/marketing/google-ads/campaigns', { customerId, campaignData });
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.error || 'Failed to create campaign');
    }
  }
);

export const updateGoogleCampaign = createAsyncThunk(
  'googleAds/updateCampaign',
  async ({ id, campaignData }, { rejectWithValue }) => {
    try {
      const res = await api.put(`/marketing/google-ads/campaigns/${id}`, { campaignData });
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.error || 'Failed to update campaign');
    }
  }
);

export const updateGoogleCampaignStatus = createAsyncThunk(
  'googleAds/updateStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const res = await api.patch(`/marketing/google-ads/campaigns/${id}/status`, { status });
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.error || 'Failed to update status');
    }
  }
);

export const deleteGoogleCampaign = createAsyncThunk(
  'googleAds/deleteCampaign',
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.delete(`/marketing/google-ads/campaigns/${id}`);
      return { ...res.data, _id: id };
    } catch (e) {
      return rejectWithValue(e.response?.data?.error || 'Failed to delete campaign');
    }
  }
);

export const syncGoogleInsights = createAsyncThunk(
  'googleAds/syncInsights',
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.post(`/marketing/google-ads/campaigns/${id}/sync`);
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.error || 'Failed to sync insights');
    }
  }
);

// ─── Ad Groups ────────────────────────────────────────────────────────────────

export const fetchAdGroups = createAsyncThunk(
  'googleAds/fetchAdGroups',
  async (params = {}, { rejectWithValue }) => {
    try {
      const res = await api.get('/marketing/google-ads/ad-groups', { params });
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.error || 'Failed to fetch ad groups');
    }
  }
);

export const createAdGroup = createAsyncThunk(
  'googleAds/createAdGroup',
  async (data, { rejectWithValue }) => {
    try {
      const res = await api.post('/marketing/google-ads/ad-groups', data);
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.error || 'Failed to create ad group');
    }
  }
);

export const updateAdGroup = createAsyncThunk(
  'googleAds/updateAdGroup',
  async ({ id, ...data }, { rejectWithValue }) => {
    try {
      const res = await api.put(`/marketing/google-ads/ad-groups/${id}`, data);
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.error || 'Failed to update ad group');
    }
  }
);

export const deleteAdGroup = createAsyncThunk(
  'googleAds/deleteAdGroup',
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.delete(`/marketing/google-ads/ad-groups/${id}`);
      return { ...res.data, _id: id };
    } catch (e) {
      return rejectWithValue(e.response?.data?.error || 'Failed to delete ad group');
    }
  }
);

export const addKeywordsToAdGroup = createAsyncThunk(
  'googleAds/addKeywords',
  async ({ id, keywords }, { rejectWithValue }) => {
    try {
      const res = await api.post(`/marketing/google-ads/ad-groups/${id}/keywords`, { keywords });
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.error || 'Failed to add keywords');
    }
  }
);

export const upsertAdGroupRSA = createAsyncThunk(
  'googleAds/upsertRSA',
  async ({ id, ...rsaData }, { rejectWithValue }) => {
    try {
      const res = await api.post(`/marketing/google-ads/ad-groups/${id}/rsa`, rsaData);
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.error || 'Failed to save RSA');
    }
  }
);

// ─── Analytics ────────────────────────────────────────────────────────────────

export const fetchAnalyticsDashboard = createAsyncThunk(
  'googleAds/fetchAnalyticsDashboard',
  async (params = {}, { rejectWithValue }) => {
    try {
      const res = await api.get('/marketing/google-ads/analytics/dashboard', { params });
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.error || 'Failed to fetch analytics');
    }
  }
);

export const fetchAnalyticsOverview = createAsyncThunk(
  'googleAds/fetchAnalyticsOverview',
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get('/marketing/google-ads/analytics/overview');
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.error || 'Failed to fetch overview');
    }
  }
);

export const syncAllCampaigns = createAsyncThunk(
  'googleAds/syncAllCampaigns',
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.post('/marketing/google-ads/analytics/sync-all');
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.error || 'Failed to sync campaigns');
    }
  }
);

// ─── AI Tools ─────────────────────────────────────────────────────────────────

export const generateHeadlines = createAsyncThunk(
  'googleAds/generateHeadlines',
  async (data, { rejectWithValue }) => {
    try {
      const res = await api.post('/marketing/google-ads/ai/generate-headlines', data);
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.error || 'Failed to generate headlines');
    }
  }
);

export const generateDescriptions = createAsyncThunk(
  'googleAds/generateDescriptions',
  async (data, { rejectWithValue }) => {
    try {
      const res = await api.post('/marketing/google-ads/ai/generate-descriptions', data);
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.error || 'Failed to generate descriptions');
    }
  }
);

export const getKeywordSuggestions = createAsyncThunk(
  'googleAds/getKeywordSuggestions',
  async (data, { rejectWithValue }) => {
    try {
      const res = await api.post('/marketing/google-ads/ai/keyword-suggestions', data);
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.error || 'Failed to get keyword suggestions');
    }
  }
);

export const optimizeCampaignAI = createAsyncThunk(
  'googleAds/optimizeCampaign',
  async (data, { rejectWithValue }) => {
    try {
      const res = await api.post('/marketing/google-ads/ai/optimize-campaign', data);
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.error || 'Failed to get optimization suggestions');
    }
  }
);

// ─── Conversions ──────────────────────────────────────────────────────────────

export const fetchConversions = createAsyncThunk(
  'googleAds/fetchConversions',
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get('/marketing/google-ads/conversions');
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.error || 'Failed to fetch conversions');
    }
  }
);

export const createConversion = createAsyncThunk(
  'googleAds/createConversion',
  async (data, { rejectWithValue }) => {
    try {
      const res = await api.post('/marketing/google-ads/conversions', data);
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.error || 'Failed to create conversion');
    }
  }
);

export const deleteConversion = createAsyncThunk(
  'googleAds/deleteConversion',
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.delete(`/marketing/google-ads/conversions/${id}`);
      return { ...res.data, _id: id };
    } catch (e) {
      return rejectWithValue(e.response?.data?.error || 'Failed to delete conversion');
    }
  }
);

export const fetchConversionScript = createAsyncThunk(
  'googleAds/fetchConversionScript',
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.get(`/marketing/google-ads/conversions/${id}/script`);
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.error || 'Failed to get tracking script');
    }
  }
);

// ─── Credentials ──────────────────────────────────────────────────────────────

export const fetchCredentials = createAsyncThunk(
  'googleAds/fetchCredentials',
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get('/marketing/google-ads/credentials');
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.error || 'Failed to fetch credentials');
    }
  }
);

export const saveCredentials = createAsyncThunk(
  'googleAds/saveCredentials',
  async (data, { rejectWithValue }) => {
    try {
      const res = await api.put('/marketing/google-ads/credentials', data);
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.error || 'Failed to save credentials');
    }
  }
);

export const validateCredentials = createAsyncThunk(
  'googleAds/validateCredentials',
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.post('/marketing/google-ads/credentials/validate');
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.error || 'Validation failed');
    }
  }
);

export const testCredentialsConnection = createAsyncThunk(
  'googleAds/testCredentialsConnection',
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.post('/marketing/google-ads/credentials/test-connection');
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.error || 'Connection test failed');
    }
  }
);

export const deleteCredentials = createAsyncThunk(
  'googleAds/deleteCredentials',
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.delete('/marketing/google-ads/credentials');
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.error || 'Failed to delete credentials');
    }
  }
);

export const fetchCredentialsOAuthUrl = createAsyncThunk(
  'googleAds/fetchCredentialsOAuthUrl',
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get('/marketing/google-ads/credentials/oauth-url');
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.error || 'Failed to get OAuth URL');
    }
  }
);

export const fetchLinkedAccounts = createAsyncThunk(
  'googleAds/fetchLinkedAccounts',
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get('/marketing/google-ads/credentials/linked-accounts');
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.error || 'Failed to fetch linked accounts');
    }
  }
);

// ─── Automation ───────────────────────────────────────────────────────────────

export const fetchAutomation = createAsyncThunk(
  'googleAds/fetchAutomation',
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get('/marketing/google-ads/automation');
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.error || 'Failed to fetch automation settings');
    }
  }
);

export const updateAutomation = createAsyncThunk(
  'googleAds/updateAutomation',
  async (data, { rejectWithValue }) => {
    try {
      const res = await api.put('/marketing/google-ads/automation', data);
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.error || 'Failed to update automation');
    }
  }
);

export const triggerAutomation = createAsyncThunk(
  'googleAds/triggerAutomation',
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.post('/marketing/google-ads/automation/trigger');
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.error || 'Failed to trigger automation');
    }
  }
);

export const fetchAutomationHistory = createAsyncThunk(
  'googleAds/fetchAutomationHistory',
  async (params = {}, { rejectWithValue }) => {
    try {
      const res = await api.get('/marketing/google-ads/automation/history', { params });
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.error || 'Failed to fetch automation history');
    }
  }
);

export const clearAutomationHistory = createAsyncThunk(
  'googleAds/clearAutomationHistory',
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.delete('/marketing/google-ads/automation/history');
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.error || 'Failed to clear history');
    }
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const initialState = {
  // Connection
  connected: false,
  tokenStatus: null,
  adAccounts: [],

  // Campaigns
  campaigns: [],

  // Ad Groups
  adGroups: [],

  // Analytics
  analyticsDashboard: null,
  analyticsOverview: null,

  // AI
  aiHeadlines: [],
  aiDescriptions: [],
  aiKeywords: [],
  aiSuggestions: [],

  // Conversions
  conversions: [],

  // Credentials
  credentials: null,
  credentialsLoading: false,
  credentialsError: null,
  linkedAccounts: [],

  // Automation
  automation: null,
  automationHistory: [],
  automationHistoryTotal: 0,
  automationLoading: false,

  // UI state
  loading: false,
  aiLoading: false,
  analyticsLoading: false,
  error: null,
  insightsSyncing: {},
};

const googleAdsSlice = createSlice({
  name: 'googleAds',
  initialState,
  reducers: {
    clearAIResults(state) {
      state.aiHeadlines = [];
      state.aiDescriptions = [];
      state.aiKeywords = [];
      state.aiSuggestions = [];
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // ── Generic loading helpers ────────────────────────────────────────────────
    const loading = (state) => { state.loading = true; state.error = null; };
    const failed = (state, action) => { state.loading = false; state.error = action.payload; };

    // ── OAuth ─────────────────────────────────────────────────────────────────
    builder
      .addCase(fetchGoogleOAuthUrl.pending, loading)
      .addCase(fetchGoogleOAuthUrl.fulfilled, (state, action) => {
        state.loading = false;
        state.authUrl = action.payload.authUrl;
      })
      .addCase(fetchGoogleOAuthUrl.rejected, failed);

    builder
      .addCase(checkGoogleConnection.pending, loading)
      .addCase(checkGoogleConnection.fulfilled, (state, action) => {
        state.loading = false;
        state.connected = action.payload.connected;
        if (action.payload.connected) state.tokenStatus = action.payload;
      })
      .addCase(checkGoogleConnection.rejected, (state, action) => {
        state.loading = false;
        state.connected = false;
        state.error = action.payload;
      });

    builder
      .addCase(disconnectGoogle.pending, loading)
      .addCase(disconnectGoogle.fulfilled, (state) => {
        state.loading = false;
        state.connected = false;
        state.tokenStatus = null;
        state.adAccounts = [];
        state.campaigns = [];
        state.adGroups = [];
      })
      .addCase(disconnectGoogle.rejected, failed);

    // ── Accounts ──────────────────────────────────────────────────────────────
    builder
      .addCase(fetchGoogleAccounts.pending, loading)
      .addCase(fetchGoogleAccounts.fulfilled, (state, action) => {
        state.loading = false;
        state.adAccounts = action.payload.accounts || [];
      })
      .addCase(fetchGoogleAccounts.rejected, failed);

    // ── Campaigns ─────────────────────────────────────────────────────────────
    builder
      .addCase(fetchGoogleCampaigns.pending, loading)
      .addCase(fetchGoogleCampaigns.fulfilled, (state, action) => {
        state.loading = false;
        state.campaigns = action.payload.campaigns || [];
      })
      .addCase(fetchGoogleCampaigns.rejected, failed);

    builder
      .addCase(createGoogleCampaign.pending, loading)
      .addCase(createGoogleCampaign.fulfilled, (state, action) => {
        state.loading = false;
        state.campaigns.unshift(action.payload.campaign);
      })
      .addCase(createGoogleCampaign.rejected, failed);

    builder
      .addCase(updateGoogleCampaign.pending, loading)
      .addCase(updateGoogleCampaign.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.campaigns.findIndex(c => c._id === action.payload.campaign._id);
        if (idx !== -1) state.campaigns[idx] = action.payload.campaign;
      })
      .addCase(updateGoogleCampaign.rejected, failed);

    builder
      .addCase(updateGoogleCampaignStatus.pending, loading)
      .addCase(updateGoogleCampaignStatus.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.campaigns.findIndex(c => c._id === action.payload.campaign._id);
        if (idx !== -1) state.campaigns[idx] = action.payload.campaign;
      })
      .addCase(updateGoogleCampaignStatus.rejected, failed);

    builder
      .addCase(deleteGoogleCampaign.pending, loading)
      .addCase(deleteGoogleCampaign.fulfilled, (state, action) => {
        state.loading = false;
        state.campaigns = state.campaigns.filter(c => c._id !== action.payload._id);
      })
      .addCase(deleteGoogleCampaign.rejected, failed);

    builder
      .addCase(syncGoogleInsights.pending, (state, action) => {
        state.insightsSyncing[action.meta.arg] = true;
      })
      .addCase(syncGoogleInsights.fulfilled, (state, action) => {
        state.insightsSyncing[action.payload.campaign._id] = false;
        const idx = state.campaigns.findIndex(c => c._id === action.payload.campaign._id);
        if (idx !== -1) state.campaigns[idx] = action.payload.campaign;
      })
      .addCase(syncGoogleInsights.rejected, (state, action) => {
        state.insightsSyncing[action.meta.arg] = false;
      });

    // ── Ad Groups ─────────────────────────────────────────────────────────────
    builder
      .addCase(fetchAdGroups.pending, loading)
      .addCase(fetchAdGroups.fulfilled, (state, action) => {
        state.loading = false;
        state.adGroups = action.payload.adGroups || [];
      })
      .addCase(fetchAdGroups.rejected, failed);

    builder
      .addCase(createAdGroup.pending, loading)
      .addCase(createAdGroup.fulfilled, (state, action) => {
        state.loading = false;
        state.adGroups.unshift(action.payload.adGroup);
      })
      .addCase(createAdGroup.rejected, failed);

    builder
      .addCase(updateAdGroup.pending, loading)
      .addCase(updateAdGroup.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.adGroups.findIndex(ag => ag._id === action.payload.adGroup._id);
        if (idx !== -1) state.adGroups[idx] = action.payload.adGroup;
      })
      .addCase(updateAdGroup.rejected, failed);

    builder
      .addCase(deleteAdGroup.pending, loading)
      .addCase(deleteAdGroup.fulfilled, (state, action) => {
        state.loading = false;
        state.adGroups = state.adGroups.filter(ag => ag._id !== action.payload._id);
      })
      .addCase(deleteAdGroup.rejected, failed);

    builder
      .addCase(addKeywordsToAdGroup.pending, loading)
      .addCase(addKeywordsToAdGroup.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.adGroups.findIndex(ag => ag._id === action.payload.adGroup._id);
        if (idx !== -1) state.adGroups[idx] = action.payload.adGroup;
      })
      .addCase(addKeywordsToAdGroup.rejected, failed);

    builder
      .addCase(upsertAdGroupRSA.pending, loading)
      .addCase(upsertAdGroupRSA.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.adGroups.findIndex(ag => ag._id === action.payload.adGroup._id);
        if (idx !== -1) state.adGroups[idx] = action.payload.adGroup;
      })
      .addCase(upsertAdGroupRSA.rejected, failed);

    // ── Analytics ─────────────────────────────────────────────────────────────
    builder
      .addCase(fetchAnalyticsDashboard.pending, (state) => { state.analyticsLoading = true; })
      .addCase(fetchAnalyticsDashboard.fulfilled, (state, action) => {
        state.analyticsLoading = false;
        state.analyticsDashboard = action.payload;
      })
      .addCase(fetchAnalyticsDashboard.rejected, (state) => { state.analyticsLoading = false; });

    builder
      .addCase(fetchAnalyticsOverview.pending, (state) => { state.analyticsLoading = true; })
      .addCase(fetchAnalyticsOverview.fulfilled, (state, action) => {
        state.analyticsLoading = false;
        state.analyticsOverview = action.payload.overview;
      })
      .addCase(fetchAnalyticsOverview.rejected, (state) => { state.analyticsLoading = false; });

    builder
      .addCase(syncAllCampaigns.pending, loading)
      .addCase(syncAllCampaigns.fulfilled, (state) => { state.loading = false; })
      .addCase(syncAllCampaigns.rejected, failed);

    // ── AI ────────────────────────────────────────────────────────────────────
    builder
      .addCase(generateHeadlines.pending, (state) => { state.aiLoading = true; })
      .addCase(generateHeadlines.fulfilled, (state, action) => {
        state.aiLoading = false;
        state.aiHeadlines = action.payload.headlines || [];
      })
      .addCase(generateHeadlines.rejected, (state) => { state.aiLoading = false; });

    builder
      .addCase(generateDescriptions.pending, (state) => { state.aiLoading = true; })
      .addCase(generateDescriptions.fulfilled, (state, action) => {
        state.aiLoading = false;
        state.aiDescriptions = action.payload.descriptions || [];
      })
      .addCase(generateDescriptions.rejected, (state) => { state.aiLoading = false; });

    builder
      .addCase(getKeywordSuggestions.pending, (state) => { state.aiLoading = true; })
      .addCase(getKeywordSuggestions.fulfilled, (state, action) => {
        state.aiLoading = false;
        state.aiKeywords = action.payload.keywords || [];
      })
      .addCase(getKeywordSuggestions.rejected, (state) => { state.aiLoading = false; });

    builder
      .addCase(optimizeCampaignAI.pending, (state) => { state.aiLoading = true; })
      .addCase(optimizeCampaignAI.fulfilled, (state, action) => {
        state.aiLoading = false;
        state.aiSuggestions = action.payload.suggestions || [];
      })
      .addCase(optimizeCampaignAI.rejected, (state) => { state.aiLoading = false; });

    // ── Conversions ───────────────────────────────────────────────────────────
    builder
      .addCase(fetchConversions.pending, loading)
      .addCase(fetchConversions.fulfilled, (state, action) => {
        state.loading = false;
        state.conversions = action.payload.conversions || [];
      })
      .addCase(fetchConversions.rejected, failed);

    builder
      .addCase(createConversion.pending, loading)
      .addCase(createConversion.fulfilled, (state, action) => {
        state.loading = false;
        state.conversions.unshift(action.payload.conversion);
      })
      .addCase(createConversion.rejected, failed);

    builder
      .addCase(deleteConversion.pending, loading)
      .addCase(deleteConversion.fulfilled, (state, action) => {
        state.loading = false;
        state.conversions = state.conversions.filter(c => c._id !== action.payload._id);
      })
      .addCase(deleteConversion.rejected, failed);

    // ── Credentials ───────────────────────────────────────────────────────────
    const credLoading = (state) => { state.credentialsLoading = true; state.credentialsError = null; };
    const credFailed  = (state, action) => { state.credentialsLoading = false; state.credentialsError = action.payload; };

    builder
      .addCase(fetchCredentials.pending, credLoading)
      .addCase(fetchCredentials.fulfilled, (state, action) => {
        state.credentialsLoading = false;
        state.credentials = action.payload.data;
      })
      .addCase(fetchCredentials.rejected, credFailed);

    builder
      .addCase(saveCredentials.pending, credLoading)
      .addCase(saveCredentials.fulfilled, (state, action) => {
        state.credentialsLoading = false;
        state.credentials = action.payload.data;
      })
      .addCase(saveCredentials.rejected, credFailed);

    builder
      .addCase(validateCredentials.pending, credLoading)
      .addCase(validateCredentials.fulfilled, (state, action) => {
        state.credentialsLoading = false;
        state.credentials = action.payload.data;
        state.credentialsError = action.payload.valid ? null : action.payload.error;
      })
      .addCase(validateCredentials.rejected, credFailed);

    builder
      .addCase(testCredentialsConnection.pending, credLoading)
      .addCase(testCredentialsConnection.fulfilled, (state) => { state.credentialsLoading = false; })
      .addCase(testCredentialsConnection.rejected, credFailed);

    builder
      .addCase(deleteCredentials.pending, credLoading)
      .addCase(deleteCredentials.fulfilled, (state) => {
        state.credentialsLoading = false;
        state.credentials = null;
        state.linkedAccounts = [];
      })
      .addCase(deleteCredentials.rejected, credFailed);

    builder
      .addCase(fetchLinkedAccounts.pending, credLoading)
      .addCase(fetchLinkedAccounts.fulfilled, (state, action) => {
        state.credentialsLoading = false;
        state.linkedAccounts = action.payload.data || [];
      })
      .addCase(fetchLinkedAccounts.rejected, credFailed);

    // ── Automation ────────────────────────────────────────────────────────────
    const autoLoading = (state) => { state.automationLoading = true; };
    const autoFailed  = (state) => { state.automationLoading = false; };

    builder
      .addCase(fetchAutomation.pending, autoLoading)
      .addCase(fetchAutomation.fulfilled, (state, action) => {
        state.automationLoading = false;
        state.automation = action.payload.data;
      })
      .addCase(fetchAutomation.rejected, autoFailed);

    builder
      .addCase(updateAutomation.pending, autoLoading)
      .addCase(updateAutomation.fulfilled, (state, action) => {
        state.automationLoading = false;
        state.automation = action.payload.data;
      })
      .addCase(updateAutomation.rejected, autoFailed);

    builder
      .addCase(triggerAutomation.pending, autoLoading)
      .addCase(triggerAutomation.fulfilled, (state) => { state.automationLoading = false; })
      .addCase(triggerAutomation.rejected, autoFailed);

    builder
      .addCase(fetchAutomationHistory.pending, autoLoading)
      .addCase(fetchAutomationHistory.fulfilled, (state, action) => {
        state.automationLoading = false;
        state.automationHistory      = action.payload.data || [];
        state.automationHistoryTotal = action.payload.total || 0;
      })
      .addCase(fetchAutomationHistory.rejected, autoFailed);

    builder
      .addCase(clearAutomationHistory.pending, autoLoading)
      .addCase(clearAutomationHistory.fulfilled, (state) => {
        state.automationLoading = false;
        state.automationHistory = [];
        state.automationHistoryTotal = 0;
      })
      .addCase(clearAutomationHistory.rejected, autoFailed);
  },
});

export const { clearAIResults, clearError } = googleAdsSlice.actions;
export default googleAdsSlice.reducer;
